import { useEffect, useRef, useState } from 'react';
import RFB from '@novnc/novnc';
import { Maximize2, X } from 'lucide-react';

import { getWsBaseUrl } from '../runtimeUrls';

type DesktopSession = {
  websocketPath: string;
  expiresAt: string;
  account: {
    requestId: number;
    tradingAccountId: number;
    brokerName?: string | null;
    mt5LoginMasked?: string | null;
    mt5Server?: string | null;
  };
};

export function Mt5DesktopModal({ session, onClose }: { session: DesktopSession; onClose: () => void }) {
  const screenRef = useRef<HTMLDivElement>(null);
  const rfbRef = useRef<RFB | null>(null);
  const [pin, setPin] = useState('');
  const [status, setStatus] = useState<'connecting' | 'pin' | 'connected' | 'failed'>('connecting');
  const [message, setMessage] = useState('Connecting to the isolated MT5 desktop...');

  useEffect(() => {
    if (!screenRef.current) return undefined;
    const base = new URL(getWsBaseUrl());
    const scheme = base.protocol === 'https:' ? 'wss:' : 'ws:';
    const socketUrl = `${scheme}//${base.host}${session.websocketPath}`;
    const rfb = new RFB(screenRef.current, socketUrl, { shared: true });
    rfb.scaleViewport = true;
    rfb.resizeSession = false;
    rfb.viewOnly = false;
    rfbRef.current = rfb;

    const connected = () => {
      setStatus('connected');
      setMessage('Connected. Enable Algo Trading in this MT5 terminal.');
      setPin('');
    };
    const credentials = () => {
      setStatus('pin');
      setMessage('Enter the private VNC PIN to unlock this desktop.');
    };
    const failed = (event: Event) => {
      const clean = Boolean((event as CustomEvent<{ clean?: boolean }>).detail?.clean);
      if (!clean) {
        setStatus('failed');
        setMessage('The desktop session ended unexpectedly. Close and open a fresh session.');
      }
    };
    const securityFailure = () => {
      setStatus('pin');
      setPin('');
      setMessage('Incorrect VNC PIN. Enter the correct PIN and retry.');
    };
    rfb.addEventListener('connect', connected);
    rfb.addEventListener('credentialsrequired', credentials);
    rfb.addEventListener('disconnect', failed);
    rfb.addEventListener('securityfailure', securityFailure);
    return () => {
      rfb.removeEventListener('connect', connected);
      rfb.removeEventListener('credentialsrequired', credentials);
      rfb.removeEventListener('disconnect', failed);
      rfb.removeEventListener('securityfailure', securityFailure);
      rfb.disconnect();
      rfbRef.current = null;
    };
  }, [session.websocketPath]);

  const submitPin = (event: React.FormEvent) => {
    event.preventDefault();
    if (!pin.trim() || !rfbRef.current) return;
    rfbRef.current.sendCredentials({ password: pin, username: '', target: '' });
    setMessage('Verifying VNC PIN...');
  };

  const fullScreen = () => screenRef.current?.requestFullscreen?.();

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/85 p-3" role="dialog" aria-modal="true">
      <div className="flex h-[94vh] w-full max-w-[1500px] flex-col overflow-hidden rounded-xl bg-slate-950 shadow-2xl ring-1 ring-white/15">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3 text-white">
          <div>
            <p className="text-xs text-emerald-300">ADMIN SECURE DESKTOP</p>
            <h2 className="font-semibold">{session.account.brokerName || 'MT5'} · {session.account.mt5LoginMasked} · {session.account.mt5Server}</h2>
            <p className="text-xs text-slate-400">{message}</p>
          </div>
          <div className="flex gap-2">
            <button className="rounded-md border border-white/20 p-2 hover:bg-white/10" onClick={fullScreen} title="Full screen"><Maximize2 size={18} /></button>
            <button className="rounded-md border border-white/20 p-2 hover:bg-white/10" onClick={onClose} title="Close desktop"><X size={18} /></button>
          </div>
        </div>
        <div className="relative min-h-0 flex-1 bg-black">
          <div ref={screenRef} className="h-full w-full overflow-hidden" />
          {status === 'pin' && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-950/80 p-4">
              <form onSubmit={submitPin} className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl">
                <h3 className="text-base font-semibold text-slate-900">Unlock MT5 desktop</h3>
                <p className="mt-1 text-sm text-slate-600">The PIN is used for this connection only and is not stored.</p>
                <input autoFocus type="password" value={pin} onChange={(event) => setPin(event.target.value)} className="mt-4 w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900" placeholder="VNC PIN" autoComplete="off" />
                <button type="submit" disabled={!pin.trim()} className="mt-3 w-full rounded-lg bg-emerald-600 px-4 py-2 font-semibold text-white disabled:opacity-50">Open secure desktop</button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
